import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Paperclip, Upload } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink, type ManagedFileRef } from "@/components/ui/ManagedFileLink";

export interface AgreementExtraFile {
  id: string;
  is_active: boolean;
  managed_file: string | null;
  managed_file_detail: (ManagedFileRef & { id: string; original_filename: string }) | null;
  /** Kto u nas wgrał — backend pomija to pole dla kont podwykonawcy. */
  uploaded_by_name?: string | null;
  created_at: string;
}

/**
 * „Inne pliki umowy" — wszystko, co dotyczy umowy poza samą umową, cennikiem i polisą:
 * aneksy, protokoły, korespondencja.
 *
 * Plik nie ma wersji ani podmiany — dokładamy kolejne. Znacznik „Aktywny" mówi, czy
 * dokument jeszcze obowiązuje: każdy wgrany startuje jako aktywny, a kliknięcie
 * przestawia go na nieaktywny i z powrotem. Nieaktywny NIE znika i dalej da się go
 * pobrać — aneks, który przestał obowiązywać, jest podstawą starych rozliczeń.
 *
 * Widoczność jest ta sama co skanu umowy (backend: can_read_agreement_document), więc
 * sekcję rysujemy tam, gdzie widać „Dokument umowy".
 */
export function AgreementExtraFilesSection({
  agreementId,
  canManage,
}: {
  agreementId: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  const {
    data: extraFiles = [],
    isError,
    isLoading,
  } = useQuery({
    queryKey: ["agreement-extra-files", agreementId],
    queryFn: () =>
      apiClient
        .get<{ data: AgreementExtraFile[] }>(`/agreements/${agreementId}/files/`)
        .then((r) => r.data.data),
    enabled: !!agreementId,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["agreement-extra-files", agreementId] });

  const upload = useMutation({
    mutationFn: (picked: File[]) => {
      const fd = new FormData();
      picked.forEach((f) => fd.append("files", f));
      return apiClient.post(`/agreements/${agreementId}/files/`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: () => {
      setFiles([]);
      setError(null);
      invalidate();
    },
    onError: () => setError("Nie udało się wgrać plików. Spróbuj ponownie."),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      apiClient.patch(`/agreements/${agreementId}/files/${id}/`, { is_active: isActive }),
    onSuccess: invalidate,
    onError: () => setError("Nie udało się zmienić oznaczenia pliku."),
  });

  return (
    <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2 flex items-center gap-2">
        <Paperclip size={15} className="text-gray-400 dark:text-gray-500" />
        Inne pliki umowy
        {extraFiles.length > 0 && (
          <span className="text-xs font-normal text-gray-400 dark:text-gray-500">
            ({extraFiles.length})
          </span>
        )}
      </h3>

      {/* Pusty stan nie może kłamać: „brak plików" mówimy dopiero wtedy, gdy backend
          odpowiedział. Odmowa dostępu (403/404 na umowie niejawnej) to inny komunikat. */}
      {isError ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Brak dostępu do plików tej umowy.
        </p>
      ) : isLoading ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Wczytywanie…</p>
      ) : extraFiles.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Brak dodatkowych plików.
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {extraFiles.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-2 flex-wrap text-xs text-gray-600 dark:text-gray-400"
            >
              <span
                className={`truncate max-w-[24rem] text-sm ${
                  f.is_active
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-400 dark:text-gray-500 line-through"
                }`}
              >
                {f.managed_file_detail?.original_filename ?? "plik usunięty"}
              </span>
              {canManage ? (
                <button
                  type="button"
                  disabled={toggleActive.isPending}
                  onClick={() =>
                    toggleActive.mutate({ id: f.id, isActive: !f.is_active })
                  }
                  title={
                    f.is_active
                      ? "Kliknij, aby oznaczyć jako nieaktywny (plik zostaje do pobrania)"
                      : "Kliknij, aby oznaczyć jako aktywny"
                  }
                  className="disabled:opacity-50"
                >
                  <Badge color={f.is_active ? "green" : "gray"}>
                    {f.is_active ? "Aktywny" : "Nieaktywny"}
                  </Badge>
                </button>
              ) : (
                <Badge color={f.is_active ? "green" : "gray"}>
                  {f.is_active ? "Aktywny" : "Nieaktywny"}
                </Badge>
              )}
              <ManagedFileLink
                file={f.managed_file_detail}
                icon={<FileText size={11} />}
              >
                Pobierz
              </ManagedFileLink>
              <span className="text-gray-400 dark:text-gray-500 whitespace-nowrap">
                {new Date(f.created_at).toLocaleString("pl")}
                {f.uploaded_by_name && ` · ${f.uploaded_by_name}`}
              </span>
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
          <FileDropZone
            files={files}
            onFilesChange={(fs) => {
              setFiles(fs);
              setError(null);
            }}
            multiple
            disabled={upload.isPending}
            label="Przeciągnij pliki lub kliknij, aby wybrać"
          />
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <Button
              size="sm"
              disabled={files.length === 0 || upload.isPending}
              onClick={() => files.length > 0 && upload.mutate(files)}
            >
              <Upload size={13} className="mr-1" />
              {upload.isPending ? "Wgrywanie…" : "Dodaj pliki"}
            </Button>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              Nowy plik jest domyślnie aktywny — kliknij oznaczenie, aby to zmienić.
            </span>
            {error && <span className="text-xs text-red-600">{error}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
